import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ServySparkle from "./ServySparkle";
import ChatSkeleton from "./ChatSkeleton";
import servyDuck from "../../assets/images/servy/servy-duck.png";
import useServyChat from "../../chat/useServyChat";
import { loadChatContext } from "../../chat/loadChatContext";
import { toServyLangCode } from "../../chat/servyMessages";
import { chatOptionsPath, hostChatPath } from "../../chat/chatRoutes";
import { hasServyKnowledgeBase } from "../../chat/servyKnowledgeBase";
import { useServySuggestedQuestions } from "../../chat/servySuggestedQuestions";
import { ShowHomeMenu } from "../../API/HomeMenuAPI";
import "./ServyChat.scss";

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M19 12H5M11 18l-6-6 6-6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21.4 2.6 10.6 13.4M21.4 2.6l-6.9 18.9-3.9-8.1-8.1-3.9 18.9-6.9Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="currentColor"
    />
  </svg>
);

const Avatar = () => (
  <span className="servy-chat__avatar" aria-hidden="true">
    <ServySparkle size={18} />
  </span>
);

const ServyChat = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { room_uuid } = useParams();
  const lang = sessionStorage.getItem("lang") || i18n.language || "EN";
  const isArabic = String(lang).toUpperCase() === "AR";

  const [boot, setBoot] = useState({ loading: true, error: null, context: null });
  const [draft, setDraft] = useState("");
  const listRef = useRef(null);

  // Same gate as the chooser: a direct link or history entry must not open
  // Servy for a unit without uploaded guide files (it has nothing to answer
  // from), so the guest lands in the host chat instead. Servy only connects
  // once the files are confirmed.
  const { data: unitRes, isSuccess: unitLoaded, isError: unitLookupFailed } =
    ShowHomeMenu(room_uuid, lang);
  const hasGuide = hasServyKnowledgeBase(unitRes?.data?.data);
  const noGuide = !hasGuide && (unitLoaded || unitLookupFailed);

  useEffect(() => {
    if (noGuide) navigate(hostChatPath(room_uuid), { replace: true });
  }, [noGuide, navigate, room_uuid]);

  // The chat is a full-screen layer; keep the page behind it from scrolling.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setBoot({ loading: true, error: null, context: null });
    loadChatContext(room_uuid, lang)
      .then((context) => {
        if (!cancelled) setBoot({ loading: false, error: null, context });
      })
      .catch((error) => {
        if (!cancelled) {
          setBoot({
            loading: false,
            error: error?.message || "boot_failed",
            context: null,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [room_uuid, lang]);

  const {
    status,
    messages,
    inFlight,
    historyLoading,
    sendQuestion,
    retryQuestion,
    reconnect,
  } = useServyChat({
      hostId: boot.context?.hostId,
      chatUuid: boot.context?.chatUuid,
      unitName: boot.context?.unitName,
      getLangCode: () =>
        toServyLangCode(sessionStorage.getItem("lang") || i18n.language),
      enabled: Boolean(boot.context) && hasGuide,
    });

  // Questions from the unit's own guide files, in the language Servy answers
  // in; a language switch loads that language's list. The list is keyed by
  // the chat_uuid: any other uuid gets an empty list, not an error.
  const suggestedQuestions = useServySuggestedQuestions({
    roomUuid: boot.context?.chatUuid,
    language: toServyLangCode(lang),
    enabled: hasGuide,
  });

  const canAsk = status === "ready" && !inFlight;
  const awaitingAnswer = messages.some(
    (message) => message.role === "guest" && message.status === "sent",
  );
  const unavailable = Boolean(boot.error);
  const connectionLost = status === "error";
  // Until the earlier conversation has loaded, neither the intro nor the chat shows yet.
  const loadingConversation =
    messages.length === 0 &&
    !unavailable &&
    !connectionLost &&
    (boot.loading || status !== "ready" || historyLoading);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages, awaitingAnswer]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (sendQuestion(draft)) setDraft("");
  };

  return (
    <div className={`servy-chat${isArabic ? " arabic" : ""}`}>
      <header className="servy-chat__header">
        <button
          type="button"
          className="servy-chat__back"
          onClick={() => navigate(chatOptionsPath(room_uuid))}
          aria-label={t("back")}
        >
          <BackIcon />
        </button>
        <Avatar />
        <div className="servy-chat__title">{t("Servy")}</div>
      </header>

      {(unavailable || connectionLost) && (
        <div className="servy-chat__banner" role="alert">
          <span>
            {unavailable
              ? t("Servy isn't available right now. Please try again.")
              : t("Connection lost")}
          </span>
          <button
            type="button"
            className="servy-chat__banner-action"
            onClick={() =>
              unavailable ? window.location.reload() : reconnect()
            }
          >
            {unavailable ? t("Reload") : t("Reconnect")}
          </button>
        </div>
      )}

      <div className="servy-chat__body" ref={listRef}>
        {loadingConversation ? (
          <ChatSkeleton label={t("Connecting...")} />
        ) : messages.length === 0 ? (
          <div className="servy-chat__intro">
            <img src={servyDuck} alt="" className="servy-chat__duck" />
            <div className="servy-chat__intro-title">
              {t("Ask Servy anything")}
            </div>
            {suggestedQuestions.length > 0 && (
              <div className="servy-chat__starters">
                {suggestedQuestions.map((question) => (
                  <button
                    key={question}
                    type="button"
                    className="servy-chat__starter"
                    disabled={!canAsk}
                    onClick={() => sendQuestion(question)}
                    dir="auto"
                  >
                    {question}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="servy-chat__messages" role="log" aria-live="polite">
            {messages.map((message) =>
              message.role === "guest" ? (
                <div key={message.id} className="servy-chat__row is-guest">
                  <div
                    className={`servy-chat__bubble is-guest${
                      message.status === "failed" ? " is-failed" : ""
                    }`}
                    dir="auto"
                  >
                    {message.text}
                  </div>
                  {message.status === "pending" && (
                    <div className="servy-chat__meta">{t("Sending...")}</div>
                  )}
                  {message.status === "failed" && (
                    <div className="servy-chat__meta is-failed">
                      {t("Servy couldn't answer. Please try again.")}
                      <button
                        type="button"
                        className="servy-chat__retry"
                        disabled={!canAsk}
                        onClick={() => retryQuestion(message.id)}
                      >
                        {t("Retry")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div key={message.id} className="servy-chat__row is-servy">
                  <Avatar />
                  <div className="servy-chat__bubble is-servy" dir="auto">
                    {message.text}
                  </div>
                </div>
              ),
            )}
            {awaitingAnswer && (
              <div className="servy-chat__row is-servy">
                <Avatar />
                <div
                  className="servy-chat__bubble is-servy servy-chat__typing"
                  aria-label={t("Servy is typing")}
                >
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <form className="servy-chat__composer" onSubmit={handleSubmit}>
        <input
          type="text"
          className="servy-chat__input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={t("Ask anything about your stay...")}
          enterKeyHint="send"
          dir="auto"
          disabled={status !== "ready"}
          aria-label={t("Ask anything about your stay...")}
        />
        <button
          type="submit"
          className="servy-chat__send"
          disabled={!canAsk || !draft.trim()}
          aria-label={t("Send")}
        >
          <SendIcon />
        </button>
      </form>
    </div>
  );
};

export default ServyChat;
